import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, Typography, Link, Tooltip, IconButton, TextField } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getGlobalCheckpoints } from "../../features/globalCheckpoints/globalCheckpointsAction";
import PaginatedTable from "../Common/Tables/table";
import Dialog from "../Common/Dialog";
import { IconPencil, IconEye } from "@tabler/icons-react";
import request from "../../request/request";
import { toast } from "react-toastify";

const GlobalCheckpoints = () => {
  const dispatch = useDispatch();
  const { data, loading } = useSelector((state) => state.globalCheckpoints);
  const [viewText, setViewText] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(getGlobalCheckpoints());
  }, [dispatch]);

  const handleEdit = (row) => {
    setEditRow(row);
    setEditValue(row.aiHelpText || "");
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await request(`/api/v1/tenants/unique-checkpoints/${editRow.id}`, {
        method: "PUT",
        body: { aiHelpText: editValue },
        auth: true,
      });
      toast.success("AI Help Text updated successfully!");
      setEditRow(null);
      dispatch(getGlobalCheckpoints());
    } catch (error) {
      toast.error(error?.message || "Failed to update AI Help Text");
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Checkpoint ID",
        accessor: "name",
        Cell: ({ value }) => (
          <Typography variant="body2" sx={{ whiteSpace: "nowrap", fontWeight: 500 }}>
            {value}
          </Typography>
        ),
      },
      {
        Header: "Title",
        accessor: "title",
        Cell: ({ value }) => (
          <Tooltip title={value || ""} arrow placement="top">
            <Typography
              variant="body2"
              sx={{
                maxWidth: 250,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {value || "—"}
            </Typography>
          </Tooltip>
        ),
      },
      {
        Header: "Tenant",
        accessor: "tenantName",
        Cell: ({ value }) => (
          <Chip label={value} size="small" variant="filled" color="info" />
        ),
      },
      {
        Header: "Score",
        accessor: "score",
        Cell: ({ value }) => (
          <Chip
            label={value}
            size="small"
            color={value >= 4 ? "error" : value >= 2 ? "warning" : "primary"}
            variant="outlined"
          />
        ),
      },
      {
        Header: "AI Help Text",
        accessor: "aiHelpText",
        Cell: ({ value }) =>
          value ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                {value.length > 50 ? value.slice(0, 50) + "..." : value}
              </Typography>
              {value.length > 50 && (
                <Link
                  component="button"
                  variant="body2"
                  sx={{ whiteSpace: "nowrap", cursor: "pointer" }}
                  onClick={() => setViewText(value)}
                >
                  View more
                </Link>
              )}
            </Box>
          ) : (
            <Typography variant="body2" color="text.disabled">—</Typography>
          ),
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {row.original.aiHelpText && (
              <Tooltip title="View AI Help Text">
                <IconButton size="small" color="info" onClick={() => setViewText(row.original.aiHelpText)}>
                  <IconEye size="1.4rem" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Edit AI Help Text">
              <IconButton size="small" color="primary" onClick={() => handleEdit(row.original)}>
                <IconPencil size="1.4rem" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    []
  );

  return (
    <Box sx={{ width: "100%", padding: "10px", marginTop: "65px" }}>
      <PaginatedTable
        columns={columns}
        data={data || []}
        totalCount={data?.length || 0}
        heading="Global Checkpoints"
        hideFeatureButton
        paginated={false}
        pageOffset={0}
        limit={data?.length || 10}
        isFetching={loading}
        setLimitFromParents={() => {}}
        setPageOffsetFromParents={() => {}}
      />

      {/* View Dialog */}
      <Dialog
        isOpen={!!viewText}
        handleClose={() => setViewText(null)}
        cancelSubmitHandle={() => setViewText(null)}
        title="AI Help Text"
      >
        <Box
          sx={{
            padding: 2,
            border: "1px solid #ccc",
            borderRadius: "8px",
            backgroundColor: "#f9f9f9",
            whiteSpace: "pre-wrap",
          }}
        >
          <Typography variant="body1">{viewText}</Typography>
        </Box>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        isOpen={!!editRow}
        handleClose={() => setEditRow(null)}
        cancelSubmitHandle={() => setEditRow(null)}
        cancelBtnText="Cancel"
        okBtnText={saving ? "Saving..." : "Save"}
        okSubmitHandle={handleSave}
        isOkBtnDisable={saving}
        title={`Edit AI Help Text — ${editRow?.name || ""}`}
      >
        <TextField
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          placeholder="Enter AI Help Text..."
        />
      </Dialog>
    </Box>
  );
};

export default GlobalCheckpoints;
